//AreaSelectingTable
import React, { useEffect, useMemo, useState, ChangeEvent } from "react";
import { AreasTablePropsTypes } from "../../popupComp/PopupTypes";
import { FlatAdmin } from "../../createWarningComp/CreateWarningTypes";
import { DownIcon, UpIcon } from "../../iconSet/IconSet";
import { useAdminData } from "../../../context/LocationDataContext";

const UK_LEVEL_NAMES: Record<number, string> = {
	1: "Country",
	2: "County",
	3: "District",
	4: "Ward",
};

const UK_LEVELS = [1, 2, 3, 4] as const;

type UkRow = FlatAdmin & { location: string };
type SortKey = "name" | "adminLevel" | "location";

const GRID = `4rem 1.2fr 1fr 2fr`;

const AreasTable = (props: AreasTablePropsTypes) => {
	const { data } = useAdminData();

	const [numberOfRows, setNumberOfRows] = useState<number>(10);
	const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
	const [search, setSearch] = useState<string>("");
	const [sortKey, setSortKey] = useState<SortKey>("name");
	const [sortAsc, setSortAsc] = useState<boolean>(true);
	const [selectedPagination, setSelectedPagination] = useState<number>(0);

	const loading = !data;

	// Build table rows from the shared map data
	const tableData = useMemo<UkRow[]>(() => {
		if (!data) return [];

		// code -> name lookup across all levels
		const nameByCode = new Map<string, string>();
		UK_LEVELS.forEach((level) =>
			((data as any)[level]?.features ?? []).forEach((f: any) => {
				const p = f.properties ?? {};
				if (p.code) nameByCode.set(String(p.code), p.name ?? "");
			})
		);

		const flat: UkRow[] = [];
		UK_LEVELS.forEach((level) => {
			const levelName = UK_LEVEL_NAMES[level];

			((data as any)[level]?.features ?? []).forEach((f: any) => {
				const p = f.properties ?? {};
				if (!p.code) return;

				const code = String(p.code);

				// nearest parent first: district -> county -> country
				const pathCodes = [p.adm3, p.adm2, p.adm1]
					.filter((c) => c && String(c) !== code)
					.map(String)
					.filter((c, idx, arr) => arr.indexOf(c) === idx);

				flat.push({
					code,
					name: p.name ?? "",
					adminLevel: p.type ?? levelName,
					location: pathCodes
						.map((c) => nameByCode.get(c))
						.filter(Boolean)
						.join(" › "),
				} as UkRow);
			});
		});

		return flat;
	}, [data]);

	// Sync incoming selection from parent
	useEffect(() => {
		if (!props.areas) return;
		const same =
			props.areas.length === selectedCodes.size &&
			props.areas.every((code) => selectedCodes.has(code));
		if (!same) setSelectedCodes(new Set(props.areas));
	}, [props.areas]);

	useEffect(() => {
		props.onSelect(Array.from(selectedCodes));
	}, [selectedCodes]);

	// Search + sort from the full data every time
	const filterTableData = useMemo(() => {
		const q = search.trim().toLowerCase();
		const filtered = q
			? tableData.filter(
				(a) =>
					a.name.toLowerCase().includes(q) ||
					a.adminLevel.toLowerCase().includes(q) ||
					a.location.toLowerCase().includes(q) ||
					a.code.toLowerCase().includes(q)
			)
			: tableData;

		return [...filtered].sort((a, b) => {
			let cmp = (a[sortKey] ?? "").localeCompare(b[sortKey] ?? "");
			if (cmp === 0) cmp = a.location.localeCompare(b.location);
			return sortAsc ? cmp : -cmp;
		});
	}, [tableData, search, sortKey, sortAsc]);

	const pagination = useMemo(() => {
		const pages: UkRow[][] = [];
		for (let i = 0; i < filterTableData.length; i += numberOfRows) {
			pages.push(filterTableData.slice(i, i + numberOfRows));
		}
		return pages;
	}, [filterTableData, numberOfRows]);

	useEffect(() => {
		setSelectedPagination((prev) => (prev < pagination.length ? prev : 0));
	}, [pagination.length]);

	const handleSort = (key: SortKey) => {
		if (sortKey === key) {
			setSortAsc((prev) => !prev);
		} else {
			setSortKey(key);
			setSortAsc(true);
		}
	};

	const sortIcon = (key: SortKey) =>
		sortKey === key && !sortAsc ? (
			<DownIcon width="1rem" color="var(--color-main-white)" />
		) : (
			<UpIcon width="1rem" color="var(--color-main-white)" />
		);

	const handleSliceCountChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = Number(e.target.value);
		if (value > 0) setNumberOfRows(value);
	};

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
		setSelectedPagination(0);
	};

	const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = e.target;
		setSelectedCodes((prev) => {
			const updated = new Set(prev);
			checked ? updated.add(name) : updated.delete(name);
			return updated;
		});
	};

	const allFilteredSelected =
		filterTableData.length > 0 && filterTableData.every((a) => selectedCodes.has(a.code));

	const handleSelectAllCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setSelectedCodes((prev) => {
			const updated = new Set(prev);
			filterTableData.forEach((a) => (checked ? updated.add(a.code) : updated.delete(a.code)));
			return updated;
		});
	};

	const paginationNavigation = (side: "next" | "prev") => {
		if (side === "next" && selectedPagination + 1 < pagination.length) {
			setSelectedPagination((p) => p + 1);
		} else if (side === "prev" && selectedPagination > 0) {
			setSelectedPagination((p) => p - 1);
		}
	};

	const groupStart = Math.floor(selectedPagination / 6) * 6;

	return (
		<div style={{ width: "100%" }} className="subTable-wrapper">
			<span className="main-title">Select Areas</span>
			<div className="field-wrapper">
				<div className="left">
					<span>
						show{" "}
						<input type="number" defaultValue={numberOfRows} min={1} onChange={handleSliceCountChange} />{" "}
						Entries
					</span>
				</div>
				<div className="right">
					<span>
						{" "}search{" "}
						<input type="text" value={search} onChange={handleSearchChange} placeholder="Search here" />
					</span>
				</div>
			</div>

			<div className="table-body">
				<div className="table-wrapper">
					<div className="table-row header" style={{ gridTemplateColumns: GRID, width: "100%" }}>
						<span>
							<input
								type="checkbox"
								className="select-all-checkbox"
								checked={allFilteredSelected}
								onChange={handleSelectAllCheckbox}
							/>
						</span>
						<span onClick={() => handleSort("name")}>
							Area
							<div>{sortIcon("name")}</div>
						</span>
						<span onClick={() => handleSort("adminLevel")}>
							Administration Level
							<div>{sortIcon("adminLevel")}</div>
						</span>
						<span onClick={() => handleSort("location")}>
							Location
							<div>{sortIcon("location")}</div>
						</span>
					</div>
				</div>

				{pagination[selectedPagination]?.map((area) => (
					<div key={area.code} className="table-wrapper">
						<div className="table-row" style={{ gridTemplateColumns: GRID, width: "100%" }}>
							<span style={{ justifyContent: "center" }}>
								<input
									type="checkbox"
									name={area.code}
									checked={selectedCodes.has(area.code)}
									onChange={handleCheckboxChange}
								/>
							</span>
							<span title={area.code}>{area.name || "N/A"}</span>
							<span>{area.adminLevel || "N/A"}</span>
							<span style={{ opacity: 0.8 }}>{area.location || "-"}</span>
						</div>
					</div>
				))}

				{loading && (
					<div className="table-no-data-wrapper">
						<span>loading areas...</span>
					</div>
				)}
				{!loading && pagination.length === 0 && (
					<div className="table-no-data-wrapper">
						<span>no any data available</span>
					</div>
				)}
			</div>

			<div className="table-pagination-wrapper">
				<div className="page-wrapper">
					{selectedPagination >= 6 && (
						<span className="icon-page-box" onClick={() => setSelectedPagination(Math.max(0, groupStart - 6))}>
							&lt;&lt;
						</span>
					)}
					<div className="icon-page-box" onClick={() => paginationNavigation("prev")}>&lt;</div>

					{pagination.slice(groupStart, groupStart + 6).map((_, idx) => {
						const pageNumber = groupStart + idx;
						return (
							<span
								key={pageNumber}
								className={selectedPagination === pageNumber ? "icon-page-box selected" : "icon-page-box"}
								onClick={() => setSelectedPagination(pageNumber)}
							>
								{pageNumber + 1}
							</span>
						);
					})}

					<div className="icon-page-box" onClick={() => paginationNavigation("next")}>&gt;</div>

					{groupStart + 6 < pagination.length && (
						<span
							className="icon-page-box"
							onClick={() => setSelectedPagination(Math.min(pagination.length - 1, groupStart + 6))}
						>
							&gt;&gt;
						</span>
					)}
				</div>
			</div>
		</div>
	);
};

export default AreasTable;